import React, { useState, useRef, ChangeEvent, useEffect } from "react";
import { InputComponent } from "../../../core/Forms/InputComponent";
import * as UI from "../../../common";
import {
  ProfileValidationResults,
  validateProfileForm,
} from "../../../../modules/profile";
import { DropdownComponent } from "../../../core/Forms/DropdownComponent";
import { IUpdateProfile } from "../../../../@types/profile";
import { INDUSTRIES, SIZES } from "../../../../constants/industries";
import LocationSelector from "../../../common/Address";
import PhoneInputComponent from "../../../common/PhoneInputComponent";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "../../../../state/useCompanyStore";
import { useUserStore } from "../../../../state";
import { showToast } from "../../../../utils/toastUtils";
import { useNavigate } from "react-router";
import { CirclePicker } from "react-color";
import PlusIcon from "../../../../assets/icons/PlusIcon";
import { generateDistinctColorPalette } from "../../../../utils/generateColor";
import RegistrationCodeInput from "../../../core/Forms/RegistrationCodeInput";
import RegistrationCodeSection from "../../../core/Forms/RegistrationCodeInput/RegistrationCodeSection";
import { verifyCode } from "../../../../utils/verifyCode";
import { DEBOUNCE_DELAY } from "../../../../constants/todo";
import PageLoader from "../../../common/PageLoader";

interface EditProfileProps {
  show: boolean;
  onSubmit: (profile: IUpdateProfile) => Promise<JSX.Element | void>;
  onClick?: (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  isLoading: boolean;
  onClose?: () => void;
  data: FormData;
}

interface Option {
  code: string;
  label: string;
}

interface FormData {
  firstName: string;
  lastName: string;
  businessName: string;
  industry: string;
  phone: string;
  email?: string;
  address: string;
  state: string;
  city: string;
  businessSize: string;
  brandColor: string;
  code: string;

  isVerified: boolean;
}
const MAX_FILE_SIZE_KB = 2048;

const EditProfile = ({
  show,
  onSubmit,
  onClose,
  data,
  isLoading,
}: EditProfileProps) => {
  const { company: user } = useCompanyStore();
  const navigate = useNavigate();
  const { t } = useTranslation("settings");
  const [formData, setFormData] = React.useState<FormData>({
    firstName: user.firstName,
    lastName: user.lastName,
    businessName: user.businessName,
    industry: user.industry || "",
    phone: user.phone,
    address: user.address,
    state: user.state || "",
    city: user.city || "",
    code: user.code || "",
    businessSize: user.businessSize || "",
    brandColor: user.brandColor || "",

    isVerified: user.isVerified,
  });

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        businessName: user.businessName || "",
        industry: user.industry || "",
        phone: user.phone || "",
        address: user.address || "",
        state: user.state || "",
        city: user.city || "",
        code: user.code || "",
        businessSize: user.businessSize || "",
        brandColor: user.brandColor || "",

        isVerified: user.isVerified || false,
      });
    }
  }, [user]);

  const [selectedValue, setSelectedValue] = useState(user.industry);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [selectedValue2, setSelectedValue2] = useState(user.businessSize);
  const [state, setState] = React.useState(user?.state || "");
  const [city, setCity] = React.useState(user?.city || "");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [rcType, setRcType] = useState("");
  const [rcPrefix, setRcPrefix] = useState("");
  const [registrationCode, setRegistrationCode] = useState("");
  const [isSelected, setIsSelected] = useState(user?.code !== "");
  // Store email separately
  const [file, setFile] = React.useState<File | null>();
  const [fileName, setFileName] = React.useState<string>("");
  const [error, setError] = React.useState<string>();

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    //  setEditedData({ ...editedData, [name]: value });
  };

  const userStore = useCompanyStore();
  const [isLoggedIn, setIsLoggedIn] = React.useState<boolean>(!!user);

  const userData = userStore.company;

  // Fetch email from the store
  const [email, setEmail] = React.useState<string>(userData?.email || "");

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return; // Prevent multiple submissions

    setIsSubmitting(true);

    const { email, ...formDataWithoutEmail } = formData;

    const validationResults = validateProfileForm(formDataWithoutEmail);
    setValidationResults(validationResults);
    if (Object.keys(validationResults).length === 0) {
      console.log("formDataWithoutEmail", formDataWithoutEmail);
      onSubmit(formDataWithoutEmail);
    } else {
      showToast("Please fill in the required fields.", "error");
    }
    setIsSubmitting(false);
  };

  // Update login status
  React.useEffect(() => {
    setIsLoggedIn(!!user);
    setEmail(userStore.company?.email || "");
  }, [user, userStore.company?.email]);

  const dropDownArray = INDUSTRIES;
  const dropDownArray2 = SIZES;
  const handleDropDownChange = (name: string, selectedValue: string) => {
    setFormData({ ...formData, industry: selectedValue });
  };

  const handleDropDownChange2 = (name: string, selectedValue2: string) => {
    setFormData({ ...formData, businessSize: selectedValue2 });
  };

  const handleLocationChange = (
    selectedState: string,
    cities: string | any[],
  ) => {
    setState(selectedState);
    setCity(cities.length > 0 ? cities[0] : "");

    setFormData((prevData) => ({
      ...prevData,
      state: selectedState,
      city: cities.length > 0 ? cities[0] : "",
    }));
  };
  const [validationResults, setValidationResults] =
    useState<ProfileValidationResults>({
      firstName: "",
      lastName: "",
      phone: "",
      businessName: "",
    });

  const handleSelectFile = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    inputRef.current?.click();
  };
  const [localLogoUrl, setLocalLogoUrl] = useState<string | undefined>();
  const handleUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files ? e.target.files[0] : null;
    if (selectedFile) {
      const fileSizeInKB = selectedFile.size / 1024;
      if (fileSizeInKB > MAX_FILE_SIZE_KB) {
        setError(`File size too large`);
        showToast(`File size should not exceed 2MB`, "info");
        setFile(null);
        setFileName("");
        return;
      }

      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!allowedTypes.includes(selectedFile.type)) {
        setError("Please upload a PNG, or JPG");
        showToast(`Please upload a PNG, or JPG`, "info");
        setFile(null);
        setFileName("");
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target && typeof event.target.result === "string") {
          const result = event.target.result as string;
          setLocalLogoUrl(result);
          setFormData((prevData) => ({
            ...prevData,
            brandLogo: result,
          }));
        }
      };
      reader.onerror = (event) => {
        console.error("Error reading the file", event.target?.error);
      };
      reader.readAsDataURL(selectedFile);

      setFile(selectedFile);
      setFileName(selectedFile.name);
      setError(undefined);
    }
  };
  const colors = generateDistinctColorPalette();
  const [selectedColor, setSelectedColor] = useState<string | undefined>(
    user.brandColor || undefined,
  );
  const handleColorChange = (color: any) => {
    setSelectedColor(color.hex);
    setFormData((prevData) => ({
      ...prevData,
      brandColor: color.hex,
    }));
  };

  const handlePhoneNumberChange = (phone: any) => {
    setFormData({ ...formData, phone: phone || "" });
  };

  const handleRegistrationCodeChange = (option: Option) => {
    setRcPrefix(option.code);
    setRcType(option.label);
    setRegistrationCode("");
    setError("");
  };

  const handleOptionSelect = () => {
    setIsSelected(true);
  };
  const [verificationMessage, setVerificationMessage] = useState<string | null>(
    null,
  );
  const validCodeLength = [8, 9, 10];
  useEffect(() => {
    const handleRcVerification = async () => {
      if (validCodeLength.includes(formData.code.length)) {
        setVerificationMessage("Verifying registration code...");
        const result = await verifyCode({
          rcnumber: registrationCode,
          businessType: rcType,
        });
        setVerificationMessage(result);
      } else {
        setVerificationMessage(null);
      }
    };

    if (validCodeLength.includes(registrationCode.length)) {
      const handler = setTimeout(handleRcVerification, DEBOUNCE_DELAY);
      return () => clearTimeout(handler);
    } else {
      setVerificationMessage(null);
    }
  }, [formData.code, rcType]);
  const displayLogo = localLogoUrl || user?.logoBase64 || user?.logoUrl;

  if (!user?.id) {
    return <PageLoader />;
  }
  return (
    <form onSubmit={handleFormSubmit}>
      <div className="flex flex-col i">
        <div className="flex flex-col  md:flex-row gap-[16px] md:gap-[14px] mb-[16px] md:mb-[16px]">
          <div className="flex flex-col md:flex-row md:gap-[3em] gap-[1em] w-full">
            <div className="">
              <h1 className="font-semibold">Personal info</h1>
              <p className="xl:w-[400px] text-[14px] text-gray-600">
                Update your personal information here
              </p>
            </div>

            <div className="flex flex-col w-full">
              <div className="flex flex-col md:flex-row md:gap-[1.4em]  w-full">
                <div className="flex flex-col">
                  <label
                    htmlFor="first-name"
                    className="text-[14px] font-medium "
                  >
                    {t("Settings.firstname")}{" "}
                    <span className="text-red-600">*</span>
                  </label>
                  <div className="">
                    <InputComponent
                      name="firstName"
                      value={formData.firstName}
                      className="border bg-[#F2F2F2] placeholder:text-[1rem] placeholder:text-[#98A2B3] placeholder:font-normal w-full  md:w-[210px] h-[48px] border-gray-300 rounded lg:w-[275px]"
                      type="text"
                      placeholder="First name"
                      handleChange={handleInputChange}
                    />
                    {validationResults.firstName && (
                      <p
                        className="error"
                        id="firstName-error"
                        style={{ color: "red" }}
                      >
                        {validationResults.firstName}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col mt-2 md:mt-0 lg:mt-0 xl:mt-0">
                  <label
                    htmlFor="last-name"
                    className="text-[14px] font-medium  "
                  >
                    {t("Settings.lastname")}{" "}
                    <span className="text-red-600">*</span>
                  </label>
                  <div className="">
                    <InputComponent
                      name="lastName"
                      className="border bg-[#F2F2F2] placeholder:text-[1rem] placeholder:text-[#98A2B3]  placeholder:font-normal h-[48px] w-full border-gray-300  rounded md:w-[210px] lg:w-[275px]"
                      type="text"
                      placeholder="Last name"
                      value={formData.lastName}
                      handleChange={handleInputChange}
                    />
                    {validationResults.lastName && (
                      <p
                        className="error"
                        id="lastName-error"
                        style={{ color: "red" }}
                      >
                        {validationResults.lastName}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:gap-[1.4em]">
                <div className="flex flex-col  mb-[14px] mt-3">
                  <label htmlFor="email" className="text-[14px] font-medium  ">
                    {t("Settings.email")} address{" "}
                    <span className="text-red-600">*</span>
                  </label>
                  <div className="">
                    <InputComponent
                      name="email"
                      className="border  placeholder:text-[1rem] placeholder:text-[#98A2B3] placeholder:font-normal h-[48px] bg-[#F2F2F2] border-gray-300 rounded  w-full md:w-[210px] lg:w-[275px]"
                      type="text"
                      placeholder="Enter email"
                      value={email}
                      handleChange={handleInputChange}
                      disabled
                      readonly
                    />
                  </div>
                </div>

                <div className="flex flex-col mb-[] md:mt-3  w-full md:w-[210px] lg:w-[275px]">
                  <label htmlFor="phone" className="text-[14px]  font-normal">
                    Phone Number <span className="text-red-600">*</span>
                  </label>
                  <PhoneInputComponent
                    onPhoneNumberChange={handlePhoneNumberChange}
                    inputClass="text-[#00000] w-full!important"
                    containerClass="setting_edit "
                    value={formData.phone}
                  />

                  {validationResults.phone && (
                    <p
                      className="error text-[16px]"
                      id="phone-error"
                      style={{ color: "red" }}
                    >
                      {validationResults.phone}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <hr className="my-6 md:hidden" />

      <div className="flex flex-col md:flex-row md:gap-[3em] gap-[1em]  md:mt-[2em] w-full">
        <div className="">
          <h1 className="font-semibold">Business info</h1>
          <p className="xl:w-[400px] lg:w-[200px] md:w-[140px] text-[14px] text-gray-600">
            Update your business information here
          </p>
        </div>

        <div className="flex flex-col">
          <div className="flex flex-col mb-[] mt-4 w-full">
            <label htmlFor="business-name" className="text-[14px]  font-medium">
              Business Name <span className="text-red-600">*</span>
            </label>
            <div className="">
              <InputComponent
                name="businessName"
                className="border bg-[#F2F2F2] placeholder:text-[1rem] placeholder:text-[#98A2B3] border-gray-300 placeholder:font-normal h-[48px] rounded w-full"
                type="text"
                value={formData.businessName}
                handleChange={handleInputChange}
                placeholder="Business Name"
              />
              {validationResults.businessName && (
                <p className="error" id="name-error" style={{ color: "red" }}>
                  {validationResults.businessName}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:gap-[1.4em]">
            <div className="flex flex-col mb-[] mt-4 w-full">
              <label htmlFor="industry" className="text-[14px]  font-medium ">
                {t("Settings.category")} <span className="text-red-600">*</span>
              </label>
              <div className="">
                <DropdownComponent
                  options={INDUSTRIES}
                  placeholder="Select category"
                  selectedValue={selectedValue}
                  setSelectedValue={setSelectedValue}
                  onChange={handleDropDownChange}
                  className="border bg-[#F2F2F2] placeholder:text-[1rem] placeholder:text-[#98A2B3] placeholder:font-normal h-[48px] border-gray-300   w-[210px] lg:w-[275px] rounded "
                  iconClassName="ml-[-2em] mt-[4px]"
                  dropdownClassName=" w-[280px] border-gray-300 rounded-md placeholder-gray-400 placeholder-opacity-75 md:w-[210px] lg:w-[275px]  bg-white  rounded py-1 focus:ring-0 pointer-events placeholder-gray-400 placeholder-opacity-75"
                  enableSearch={true}
                />
              </div>
            </div>

            <div className="flex flex-col mb-[] mt-2 w-full">
              <label
                htmlFor="address"
                className="text-[14px]  font-medium mt-3 pl-[px]"
              >
                {t("Settings.address")} <span className="text-red-600">*</span>
              </label>
              <div className="flex flex-col ">
                <div className="">
                  <InputComponent
                    name="address"
                    className="border bg-[#F2F2F2] placeholder:text-[1rem] placeholder:text-[#98A2B3] border-gray-300 placeholder:font-normal h-[48px]  w-full md:w-[210px] lg:w-[275px] rounded"
                    type="text"
                    value={formData.address}
                    handleChange={handleInputChange}
                    placeholder="Enter business address"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col ">
            <div className=" mt-2 lg:mt-[1em]  biglg:mt-[1em] ">
              <LocationSelector
                onLocationChange={handleLocationChange}
                onError={(error: any) => {}}
                selectedState={(state || user.state) ?? ""}
                selectedCity={(city || user.city) ?? ""}
              />
            </div>
          </div>

          <div className="w-full mt-5">
            <RegistrationCodeInput
              id="businessType"
              name="businessType"
              value={rcType}
              onChange={handleRegistrationCodeChange}
              onSelect={handleOptionSelect}
            />
            {isSelected && (
              <RegistrationCodeSection
                id="code"
                name="code"
                value={formData.code}
                rcPrefix={rcPrefix}
                onChange={(e) => {
                  setRegistrationCode(e.target.value);
                  handleInputChange(e);
                }}
                verificationResult={verificationMessage}
              />
            )}
          </div>
        </div>
      </div>

      <hr className="my-12" />

      <div className="mt-[3em] ">
        <div className="flex flex-col md:flex-row xl:gap-[14em] md:gap-[3em] ">
          <div>
            <label htmlFor="Logo" className=" font-semibold mt-5">
              Brand
            </label>
            <p className="text-[14px] text-gray-600 hidden md:block">
              Update your business brand here
            </p>
          </div>

          <div className="xl:text-[14px]   font-light  xl:ml-[em] md:mt-[3px]">
            <p className="font-medium hidden md:block">Upload Brand Logo</p>
            <div className="flex  mt-[5px]">
              <div
                onClick={handleSelectFile}
                className="flex items-center justify-center px-[13px] cursor-pointer lg:w-[530px] py-[8px] lg:h-[120px] md:w-[300px] md:h-[120px] w-full h-[100px] rounded-[4px] bg-gray-50 border-gray-300 border border-dashed "
              >
                {displayLogo ? (
                  <div className="relative w-full h-full">
                    <img
                      src={displayLogo}
                      alt="Uploaded logo preview"
                      className="w-full h-full object-contain rounded-xl"
                    />
                    {/* Edit overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <span className="text-white font-semibold text-sm">
                        Click to Edit
                      </span>
                    </div>
                  </div>
                ) : (
                  <span className="leading-6 text-[14px] font-medium text-gray-700">
                    Upload a Photo
                  </span>
                )}

                <input
                  className="hidden"
                  type="file"
                  ref={inputRef}
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleUpload}
                />
              </div>
            </div>
            {error && (
              <p className="text-red-500 text-sm font-medium">{error}</p>
            )}

            <div className="mt-6">
              <p className="text-[14px] md:text-[16px] font-medium leading-6 text-[#000000]">
                Choose a color that best fit the brand
              </p>
              <div className="my-3 h-9 w-9 relative">
                <input
                  type="color"
                  id="color-input"
                  className={`block w-full h-full rounded-md relative ${
                    !selectedColor ? "bg-white" : ""
                  }`}
                  value={selectedColor || user.brandColor} // Default to white if no color is selected
                  onChange={(e) => setSelectedColor(e.target.value)}
                  style={{
                    backgroundColor:
                      selectedColor || user.brandColor || "transparent",
                    appearance: "none", // Hides default browser styling for better customization
                    position: "absolute",
                    top: 0,
                    left: 0,
                    opacity: selectedColor ? 1 : 0, // Hide input background if no color is selected
                  }}
                />
                {!selectedColor && (
                  <div
                    className="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center pointer-events-none border-[0.5px] border-[#D0D5DD]"
                    style={{
                      zIndex: 1, // Ensure icon is above the input's transparent layer
                    }}
                  >
                    <PlusIcon color="#292D32" />
                  </div>
                )}
              </div>

              <div className="mt-[1em] hidden md:block max-w-[530px]">
                <CirclePicker
                  onChange={handleColorChange}
                  width="100%"
                  circleSize={34}
                  circleSpacing={20}
                  colors={colors}
                />
              </div>
              <div className="mt-[1em] md:hidden">
                <CirclePicker
                  onChange={handleColorChange}
                  width="100%"
                  circleSize={30}
                  circleSpacing={14}
                  colors={colors}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <hr className="mt-12" />

      <div className="xl:w-[300px] md:w-[300px]  pb-6" />
      <div className="flex justify-center items-center pb-6">
        <div className="w-full flex justify-center gap-[1em] mt-4 pb-6 xl:ml-[25em]">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-10 py-3  bg-[#F2F4F7] text-gray-700 font-semibold rounded-md hidden md:flex"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-3 border border-[#0469C5] text-[#044E97] font-semibold rounded-md hidden md:flex items-center justify-center min-w-[140px]"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="w-7 h-7 border-4 border-gray-200 border-t-[#044E97] rounded-full animate-spin"></div>
            ) : (
              "Save Changes"
            )}
          </button>

          <div className="w-full md:hidden">
            <UI.Button
              type="submit"
              text="Save Changes"
              isLoading={isLoading}
              variant="secondary"
              size="full"
            />
          </div>
        </div>
      </div>
    </form>
    // </SidebarDrawer>
  );
};

export { EditProfile };
